import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { LogIn, Eye, EyeOff, KeyRound, Mail, HelpCircle, ArrowLeft, Check, AlertTriangle, Lock, Sun, Moon } from 'lucide-react';
import api from '../services/api';

// Detect backend URL based on hostname
const getBackendUrl = () => {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  const hostname = window.location.hostname;
  if (hostname === 'cminventario.com' || hostname === 'www.cminventario.com') {
    return 'https://api.cminventario.com';
  }
  if (hostname.includes('railway.app')) {
    return `https://${hostname.replace('frontend', 'backend')}`;
  }
  return '';
};

const API_URL = getBackendUrl();

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isAccountLocked, setIsAccountLocked] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Login page customization
  const [loginConfig, setLoginConfig] = useState({
    login_logo_url: '',
    login_background_color: '#0a0a0a',
    login_card_color: '#1a1a1a',
    login_title: 'Entradas y Salidas de Equipo',
    login_subtitle: 'CM Idiomas',
    login_welcome_text: 'Ingresa tus credenciales para acceder',
    login_button_text: 'Ingresar',
    login_button_color: '#00B2A9',
    login_text_color: '#ffffff'
  });

  // Theme mode for login page
  const [isLightMode, setIsLightMode] = useState(() => document.documentElement.classList.contains('light'));

  // Password recovery state
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryStep, setRecoveryStep] = useState(1); // 1: username, 2: choose method, 3: answer/email, 4: new password
  const [recoveryUsername, setRecoveryUsername] = useState('');
  const [recoveryOptions, setRecoveryOptions] = useState(null);
  const [recoveryMethod, setRecoveryMethod] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);

  useEffect(() => {
    // Load login page configuration and style config for logo fallback
    const loadLoginConfig = async () => {
      try {
        const [loginRes, styleRes] = await Promise.all([
          fetch(`${API_URL}/api/config/login`).then(r => r.ok ? r.json() : {}),
          fetch(`${API_URL}/api/config/style`).then(r => r.ok ? r.json() : {}).catch(() => ({}))
        ]);
        
        // Merge configs: login config takes priority, but use style logo as fallback
        const merged = { ...loginRes };
        if (!merged.login_logo_url && styleRes.logo_url) {
          merged.login_logo_url = styleRes.logo_url;
        }
        
        if (Object.keys(merged).length > 0) {
          setLoginConfig(prev => ({ ...prev, ...merged }));
        }
      } catch (error) {
        console.log('Using default login config');
      }
    };
    loadLoginConfig();
    
    // Load remembered username
    const savedUsername = localStorage.getItem('rememberedUsername');
    const savedRememberMe = localStorage.getItem('rememberMe') === 'true';
    if (savedUsername && savedRememberMe) {
      setUsername(savedUsername);
      setRememberMe(true);
    }
    
    // Load failed attempts from session
    const savedAttempts = sessionStorage.getItem(`failedAttempts_${savedUsername || ''}`);
    if (savedAttempts) {
      const attempts = parseInt(savedAttempts);
      setFailedAttempts(attempts);
      if (attempts >= 10) {
        setIsAccountLocked(true);
      }
    }
  }, []);

  const handleMicrosoftLogin = () => {
    setSsoLoading(true);
    const origin = encodeURIComponent(window.location.origin);
    window.location.href = `${API_URL}/api/auth/microsoft/login?return_origin=${origin}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    // Check if account is locked
    if (isAccountLocked) {
      setLoginError('Tu cuenta ha sido bloqueada por múltiples intentos fallidos. Contacta al administrador.');
      return;
    }
    
    setLoading(true);
    try {
      const result = await login(username, password);
      
      if (result.success) {
        // Save remember me preference
        if (rememberMe) {
          localStorage.setItem('rememberedUsername', username);
          localStorage.setItem('rememberMe', 'true');
        } else {
          localStorage.removeItem('rememberedUsername');
          localStorage.removeItem('rememberMe');
        }
        
        // Clear failed attempts on success
        sessionStorage.removeItem(`failedAttempts_${username}`);
        setFailedAttempts(0);
        
        navigate('/');
      } else {
        // Handle login failure
        const newAttempts = failedAttempts + 1;
        setFailedAttempts(newAttempts);
        sessionStorage.setItem(`failedAttempts_${username}`, newAttempts.toString());
        
        // Check for account locked from server
        if (result.error?.includes('bloqueada')) {
          setIsAccountLocked(true);
          setLoginError('Tu cuenta ha sido bloqueada. Contacta al administrador para desbloquearla.');
        } else if (newAttempts >= 10) {
          // Lock account after 10 attempts
          setIsAccountLocked(true);
          setLoginError('Tu cuenta ha sido bloqueada por múltiples intentos fallidos. Contacta al administrador.');
          // Notify backend to lock account
          try {
            await fetch(`${API_URL}/api/auth/lock-account`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ username })
            });
          } catch (e) {
            console.error('Error locking account:', e);
          }
        } else if (newAttempts >= 5) {
          setLoginError(`Credenciales incorrectas. Intento ${newAttempts}/10. Te sugerimos cambiar tu contraseña.`);
        } else {
          setLoginError(`Usuario o contraseña incorrectos. Intento ${newAttempts}/10.`);
        }
      }
    } catch (error) {
      setLoginError('Error de conexión. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Check user exists and get recovery options
  const handleCheckUser = async () => {
    if (!recoveryUsername.trim()) {
      toast.error('Ingresa tu nombre de usuario');
      return;
    }

    setRecoveryLoading(true);
    try {
      const res = await api.post('/auth/recover/check-user', { username: recoveryUsername });
      setRecoveryOptions(res.data);
      setRecoveryStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Usuario no encontrado');
    } finally {
      setRecoveryLoading(false);
    }
  };

  // Step 3a: Verify security question
  const handleSecurityQuestion = async () => {
    if (!securityAnswer.trim()) {
      toast.error('Ingresa tu respuesta');
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setRecoveryLoading(true);
    try {
      await api.post('/auth/recover/security-question', {
        username: recoveryUsername,
        answer: securityAnswer,
        new_password: newPassword
      });
      toast.success('¡Contraseña actualizada! Ya puedes iniciar sesión.');
      resetRecovery();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Respuesta incorrecta');
    } finally {
      setRecoveryLoading(false);
    }
  };

  // Step 3b: Send recovery email
  const handleSendEmail = async () => {
    if (!recoveryEmail.trim()) {
      toast.error('Ingresa tu correo electrónico');
      return;
    }

    setRecoveryLoading(true);
    try {
      await api.post('/auth/recover/send-email', {
        username: recoveryUsername,
        email: recoveryEmail
      });
      toast.success('Correo enviado. Revisa tu bandeja de entrada.');
      setRecoveryStep(4);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar correo');
    } finally {
      setRecoveryLoading(false);
    }
  };

  const toggleLoginTheme = () => {
    const newLight = !isLightMode;
    setIsLightMode(newLight);
    if (newLight) {
      document.documentElement.classList.add('light');
    } else {
      document.documentElement.classList.remove('light');
    }
    localStorage.setItem('themeMode', newLight ? 'light' : 'dark');
    window.dispatchEvent(new CustomEvent('themeModeChange', { detail: newLight ? 'light' : 'dark' }));
  };

  // Compute text color: use dark text in light mode regardless of config
  const textColor = isLightMode ? '#1e293b' : loginConfig.login_text_color;
  const textColorMuted = isLightMode ? '#64748b' : `${loginConfig.login_text_color}b3`;
  const textColorSubtle = isLightMode ? '#94a3b8' : `${loginConfig.login_text_color}80`;

  const resetRecovery = () => {
    setShowRecovery(false);
    setRecoveryStep(1);
    setRecoveryUsername('');
    setRecoveryOptions(null);
    setRecoveryMethod('');
    setSecurityAnswer('');
    setRecoveryEmail('');
    setNewPassword('');
    setConfirmPassword('');
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ backgroundColor: isLightMode ? '#f0f2f5' : loginConfig.login_background_color }}
    >
      {/* Light Mode Toggle Button - Top Right */}
      <button
        onClick={toggleLoginTheme}
        className={`absolute top-5 right-5 z-50 flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-all duration-300 backdrop-blur-md ${
          isLightMode
            ? 'bg-slate-800/90 text-white hover:bg-slate-700 shadow-lg shadow-slate-300/40'
            : 'bg-white/10 text-white/80 hover:bg-white/20 border border-white/10'
        }`}
        data-testid="login-theme-toggle"
      >
        {isLightMode ? <Moon className="h-3.5 w-3.5" /> : <Sun className="h-3.5 w-3.5" />}
        {isLightMode ? 'Modo Oscuro' : 'Modo Claro'}
      </button>

      {/* Animated Background Pattern - Equipment/Tech Theme */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Gradient overlay */}
        <div className={`absolute inset-0 ${isLightMode 
          ? 'bg-[radial-gradient(ellipse_at_top_left,_hsl(175_100%_90%/0.4),_transparent_50%),radial-gradient(ellipse_at_bottom_right,_hsl(217_91%_92%/0.5),_transparent_50%)]' 
          : 'bg-gradient-to-br from-[#00B2A9]/20 via-transparent to-blue-900/20'}`} />
        
        {/* Animated floating shapes */}
        <div className="absolute inset-0">
          <div className={`absolute -top-20 -left-20 w-96 h-96 rounded-full blur-3xl animate-pulse ${isLightMode ? 'bg-teal-300/20' : 'bg-primary/10'}`} style={{animationDuration: '4s'}} />
          <div className={`absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full blur-3xl animate-pulse ${isLightMode ? 'bg-blue-200/30' : 'bg-blue-500/10'}`} style={{animationDuration: '6s', animationDelay: '1s'}} />
          <div className={`absolute top-1/2 left-1/4 w-72 h-72 rounded-full blur-3xl animate-pulse ${isLightMode ? 'bg-indigo-200/15' : 'bg-purple-500/5'}`} style={{animationDuration: '5s', animationDelay: '2s'}} />
          {isLightMode && <div className="absolute top-1/4 right-1/3 w-64 h-64 rounded-full blur-3xl animate-pulse bg-amber-200/10" style={{animationDuration: '7s', animationDelay: '3s'}} />}
        </div>
        
        {/* Grid pattern */}
        <div className="absolute inset-0" style={{
          backgroundImage: isLightMode
            ? `linear-gradient(rgba(0, 178, 169, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 178, 169, 0.04) 1px, transparent 1px)`
            : `linear-gradient(rgba(0, 178, 169, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 178, 169, 0.03) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }} />
        
        {/* Floating icons/shapes representing AV equipment */}
        <div className={`absolute inset-0 ${isLightMode ? 'opacity-[0.06] text-teal-700' : 'opacity-[0.03]'}`}>
          <svg className="absolute top-[10%] left-[15%] w-24 h-24 animate-float" style={{animationDuration: '6s'}} viewBox="0 0 24 24" fill="currentColor">
            <path d="M4 4h16a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm0 2v12h16V6H4zm2 2h2v2H6V8zm0 4h12v4H6v-4z"/>
          </svg>
          <svg className="absolute top-[30%] right-[10%] w-16 h-16 animate-float" style={{animationDuration: '7s', animationDelay: '1s'}} viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 1a9 9 0 00-9 9v7c0 1.66 1.34 3 3 3h3v-8H5v-2a7 7 0 1114 0v2h-4v8h3c1.66 0 3-1.34 3-3v-7a9 9 0 00-9-9z"/>
          </svg>
          <svg className="absolute bottom-[20%] left-[20%] w-20 h-20 animate-float" style={{animationDuration: '8s', animationDelay: '2s'}} viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM4 6h16v12H4V6z"/>
          </svg>
          <svg className="absolute top-[60%] right-[25%] w-14 h-14 animate-float" style={{animationDuration: '5s', animationDelay: '3s'}} viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
          </svg>
        </div>
        
        {/* Decorative lines */}
        <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent ${isLightMode ? 'via-[#00B2A9]/40' : 'via-[#00B2A9]/30'} to-transparent`} />
        <div className={`absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent ${isLightMode ? 'via-[#00B2A9]/40' : 'via-[#00B2A9]/30'} to-transparent`} />
      </div>
      
      {/* Add keyframes for float animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
      
      <Card 
        className={`w-full max-w-md shadow-2xl border-0 relative z-10 backdrop-blur-sm ${isLightMode ? 'bg-white/80 border border-slate-200/50 shadow-xl shadow-slate-300/30 ring-1 ring-black/[0.03]' : ''}`}
        style={isLightMode ? {} : { backgroundColor: `${loginConfig.login_card_color}ee` }}
      >
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto flex items-center justify-center mb-2">
            {loginConfig.login_logo_url ? (
              <img 
                src={loginConfig.login_logo_url} 
                alt="Logo" 
                className="h-12 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `<span class="text-2xl font-bold" style="color: ${loginConfig.login_button_color}">${loginConfig.login_subtitle}</span>`;
                }}
              />
            ) : (
              <img 
                src="https://cmidiomas.com/images/logo-default-246x44.png" 
                alt="CM Idiomas" 
                className="h-12 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<span class="text-2xl font-bold text-primary">CM Idiomas</span>';
                }}
              />
            )}
          </div>
          <div>
            <CardTitle 
              className="text-2xl font-bold"
              style={{ color: textColor }}
            >
              {loginConfig.login_subtitle}
            </CardTitle>
            <CardDescription 
              className="mt-1"
              style={{ color: textColorMuted }}
            >
              {loginConfig.login_title}
            </CardDescription>
            <p 
              className="text-xs mt-2"
              style={{ color: textColorSubtle }}
            >
              {loginConfig.login_welcome_text}
            </p>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Error Message */}
            {loginError && (
              <div className={`p-3 rounded-lg flex items-start gap-2 ${
                isAccountLocked 
                  ? 'bg-red-500/20 border border-red-500/50' 
                  : failedAttempts >= 5 
                    ? 'bg-amber-500/20 border border-amber-500/50' 
                    : 'bg-red-500/10 border border-red-500/30'
              }`}>
                {isAccountLocked ? (
                  <Lock className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                ) : (
                  <AlertTriangle className={`h-5 w-5 mt-0.5 flex-shrink-0 ${failedAttempts >= 5 ? 'text-amber-500' : 'text-red-400'}`} />
                )}
                <p className={`text-sm ${isAccountLocked ? 'text-red-400' : failedAttempts >= 5 ? 'text-amber-400' : 'text-red-400'}`}>
                  {loginError}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="username" style={{ color: textColor }}>Usuario</Label>
              <Input
                id="username"
                placeholder="Ingresa tu usuario"
                value={username}
                onChange={(e) => { setUsername(e.target.value); setLoginError(''); }}
                required
                autoComplete="username"
                data-testid="username-input"
                disabled={isAccountLocked}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" style={{ color: textColor }}>Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Ingresa tu contraseña"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setLoginError(''); }}
                  required
                  autoComplete="current-password"
                  data-testid="password-input"
                  disabled={isAccountLocked}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            {/* Remember Me Checkbox */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="rememberMe"
                checked={rememberMe}
                onCheckedChange={setRememberMe}
                disabled={isAccountLocked}
              />
              <Label 
                htmlFor="rememberMe" 
                className="text-sm cursor-pointer"
                style={{ color: textColorMuted }}
              >
                Recordar mi usuario
              </Label>
            </div>
            
            <Button 
              type="submit" 
              className="w-full text-white" 
              disabled={loading || isAccountLocked} 
              data-testid="login-button"
              style={{ backgroundColor: isAccountLocked ? '#666' : loginConfig.login_button_color }}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : isAccountLocked ? (
                <Lock className="h-4 w-4 mr-2" />
              ) : (
                <LogIn className="h-4 w-4 mr-2" />
              )}
              {isAccountLocked ? 'Cuenta Bloqueada' : loginConfig.login_button_text}
            </Button>

            <div className="text-center pt-2">
              <button
                type="button"
                className={`text-sm flex items-center justify-center gap-1 mx-auto transition-all ${
                  failedAttempts >= 5 
                    ? 'text-amber-400 font-bold animate-pulse bg-amber-500/20 px-4 py-2 rounded-lg' 
                    : 'text-primary hover:underline'
                }`}
                onClick={() => setShowRecovery(true)}
                data-testid="forgot-password-link"
              >
                <KeyRound className="h-3 w-3" />
                ¿Olvidaste tu contraseña?
              </button>
            </div>

            {/* Divider */}
            <div className="relative my-3">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border/60" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2" style={{ backgroundColor: isLightMode ? 'rgba(255,255,255,0.9)' : `${loginConfig.login_card_color}ee`, color: textColorSubtle }}>
                  o continúa con
                </span>
              </div>
            </div>

            {/* Microsoft SSO Button */}
            <Button
              type="button"
              variant="outline"
              className="w-full flex items-center justify-center gap-2 border-border/60 hover:bg-muted/30"
              onClick={handleMicrosoftLogin}
              disabled={ssoLoading}
              data-testid="microsoft-sso-button"
            >
              {ssoLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-1"></div>
              ) : (
                <svg className="h-4 w-4" viewBox="0 0 21 21" fill="none">
                  <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                  <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                </svg>
              )}
              <span style={{ color: textColor }}>
                {ssoLoading ? 'Conectando...' : 'Iniciar sesión con Microsoft'}
              </span>
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Password Recovery Dialog */}
      <Dialog open={showRecovery} onOpenChange={(open) => !open && resetRecovery()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" />
              Recuperar Contraseña
            </DialogTitle>
            <DialogDescription>
              {recoveryStep === 1 && 'Ingresa tu nombre de usuario para comenzar'}
              {recoveryStep === 2 && 'Elige cómo quieres recuperar tu contraseña'}
              {recoveryStep === 3 && recoveryMethod === 'question' && 'Responde tu pregunta secreta'}
              {recoveryStep === 3 && recoveryMethod === 'email' && 'Confirma tu correo electrónico'}
              {recoveryStep === 4 && 'Revisa tu correo electrónico'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Step 1: Enter username */}
            {recoveryStep === 1 && (
              <>
                <div className="space-y-2">
                  <Label>Nombre de usuario</Label>
                  <Input
                    placeholder="Ingresa tu usuario"
                    value={recoveryUsername}
                    onChange={(e) => setRecoveryUsername(e.target.value)}
                    autoFocus
                    data-testid="recovery-username"
                  />
                </div>
                <Button 
                  className="w-full" 
                  onClick={handleCheckUser}
                  disabled={recoveryLoading}
                >
                  {recoveryLoading ? 'Verificando...' : 'Continuar'}
                </Button>
              </>
            )}

            {/* Step 2: Choose recovery method */}
            {recoveryStep === 2 && recoveryOptions && (
              <div className="space-y-3">
                {recoveryOptions.has_security_question && (
                  <button
                    className={`w-full p-4 border rounded-lg text-left hover:bg-muted/50 transition-colors ${
                      recoveryMethod === 'question' ? 'border-primary bg-primary/5' : ''
                    }`}
                    onClick={() => {
                      setRecoveryMethod('question');
                      setRecoveryStep(3);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <HelpCircle className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-medium">Pregunta secreta</p>
                        <p className="text-sm text-muted-foreground">Responde tu pregunta de seguridad</p>
                      </div>
                    </div>
                  </button>
                )}

                {recoveryOptions.has_email && (
                  <button
                    className={`w-full p-4 border rounded-lg text-left hover:bg-muted/50 transition-colors ${
                      recoveryMethod === 'email' ? 'border-primary bg-primary/5' : ''
                    }`}
                    onClick={() => {
                      setRecoveryMethod('email');
                      setRecoveryStep(3);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-medium">Correo electrónico</p>
                        <p className="text-sm text-muted-foreground">
                          Enviar enlace a {recoveryOptions.email_masked}
                        </p>
                      </div>
                    </div>
                  </button>
                )}

                {!recoveryOptions.has_security_question && !recoveryOptions.has_email && (
                  <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                    <p className="text-yellow-700 dark:text-yellow-400">
                      No tienes opciones de recuperación configuradas. Contacta al administrador.
                    </p>
                  </div>
                )}

                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => setRecoveryStep(1)}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Volver
                </Button>
              </div>
            )}

            {/* Step 3a: Security question */}
            {recoveryStep === 3 && recoveryMethod === 'question' && recoveryOptions && (
              <div className="space-y-4">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium text-muted-foreground">Tu pregunta:</p>
                  <p className="font-medium">{recoveryOptions.security_question}</p>
                </div>

                <div className="space-y-2">
                  <Label>Tu respuesta</Label>
                  <Input
                    placeholder="Ingresa tu respuesta"
                    value={securityAnswer}
                    onChange={(e) => setSecurityAnswer(e.target.value)}
                    autoFocus
                    data-testid="security-answer"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Nueva contraseña</Label>
                  <PasswordInput
                    placeholder="Mínimo 6 caracteres"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    data-testid="new-password"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Confirmar contraseña</Label>
                  <PasswordInput
                    placeholder="Repite la contraseña"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    data-testid="confirm-password"
                  />
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setRecoveryStep(2)}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <Button 
                    className="flex-1"
                    onClick={handleSecurityQuestion}
                    disabled={recoveryLoading}
                  >
                    {recoveryLoading ? 'Verificando...' : 'Cambiar Contraseña'}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3b: Email verification */}
            {recoveryStep === 3 && recoveryMethod === 'email' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Confirma tu correo electrónico</Label>
                  <Input
                    type="email"
                    placeholder="tu@correo.com"
                    value={recoveryEmail}
                    onChange={(e) => setRecoveryEmail(e.target.value)}
                    autoFocus
                    data-testid="recovery-email"
                  />
                  <p className="text-xs text-muted-foreground">
                    Debe coincidir con el correo registrado en tu cuenta
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setRecoveryStep(2)}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <Button 
                    className="flex-1"
                    onClick={handleSendEmail}
                    disabled={recoveryLoading}
                  >
                    {recoveryLoading ? 'Enviando...' : 'Enviar Enlace'}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Email sent confirmation */}
            {recoveryStep === 4 && (
              <div className="text-center space-y-4">
                <div className="mx-auto w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">¡Correo enviado!</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Revisa tu bandeja de entrada y sigue las instrucciones del correo para restablecer tu contraseña.
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={resetRecovery}
                >
                  Volver al inicio de sesión
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
